<script setup lang="ts">
import type { Page } from '@/types/model'
import { computed, ref, nextTick } from 'vue'
import { PhCaretDown } from '@phosphor-icons/vue'
import { useFloating, shift } from '@floating-ui/vue'
import NavbarMenubarItemMenu from '@/components/navbar/NavbarMenubarItemMenu.vue'
import PhIcon from '@/components/PhIcon.vue'
import { useRouter, useRoute } from 'vue-router'

const props = defineProps<{
  data: Page
}>()

const router = useRouter()
const route = useRoute()

const page = computed(() => props.data)
const havePopover = computed(() => page.value.page_items.length > 0)
const activeMenu = computed(() => page.value.url === route.name)

const showPopoverMenubar = ref(false)

const reference = ref(null)
const floating = ref(null)
const { floatingStyles, update } = useFloating(reference, floating, {
  middleware: [shift()],
})

const togglePopover = async () => {
  if (!havePopover.value) {
    if (!page.value.url) return

    await router.push({
      name: page.value.url,
    })
    return
  }

  await nextTick()

  update()
  showPopoverMenubar.value = !showPopoverMenubar.value
}

const closePopover = () => {
  showPopoverMenubar.value = false
}
</script>

<template>
  <!-- Backdrop -->
  <Transition
    enter-active-class="transition-opacity duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="havePopover && showPopoverMenubar"
      class="fixed inset-0 z-10"
      @click="closePopover"
    />
  </Transition>

  <div
    ref="reference"
    @click="togglePopover"
    :class="activeMenu ? 'bg-blue-400/25 text-blue-600' : ''"
    class="flex gap-1 px-3 py-2 hover:bg-blue-500/25 hover:text-blue-600 rounded-xl cursor-pointer transition-all duration-150"
  >
    <span v-if="page.icon_only">
      <ph-icon v-if="page.icon" :name="page.icon"></ph-icon>
    </span>
    <span v-else class="block text-serif text-base font-semibold whitespace-nowrap">{{
      page.name
    }}</span>

    <span v-if="havePopover" class="self-center">
      <PhCaretDown :size="16" weight="duotone" />
    </span>
  </div>

  <!-- Popover -->
  <Transition
    enter-active-class="transition-opacity duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="showPopoverMenubar"
      ref="floating"
      :style="floatingStyles"
      class="bg-glass absolute top-0 left-0 w-max p-1 rounded-xl z-20"
    >
      <div class="bg-stone-100 dark:bg-stone-900 rounded-lg transition-colors duration-150">
        <navbar-menubar-item-menu
          :data="{
            name: page.name,
            desc: page.desc as string,
            url: page.url as string,
          }"
        ></navbar-menubar-item-menu>
        <navbar-menubar-item-menu
          v-for="(pageItem, i) in page.page_items"
          :key="i"
          :data="pageItem"
          :path="page.url"
        ></navbar-menubar-item-menu>
      </div>
    </div>
  </Transition>
</template>

<style scoped></style>
